import axios from 'axios';
import constants from '../constants';
import { router } from '../router';
import { deleteAuthCookie } from '../utils/cookie';
import {
    defaultTicketFilters,
    defaultFiltersDisplay,
    defaultTicketFields,
} from './ticketDataHelpers';
import { guidNormal } from '@/utils/guid.js';

const NEWTIN_HOST = constants.NEWTIN_DOMAIN || '';
const NEWTIN_GEOCODER = constants.NEWTIN_GEOCODER || '';
axios.defaults.withCredentials = true;

// Add a response interceptor
axios.interceptors.response.use(
    function (response) {
        // Any status code that lie within the range of 2xx cause this function to trigger
        // Do something with response data
        return response;
    },
    function (error) {
        // Any status codes that falls outside the range of 2xx cause this function to trigger
        // Do something with response error
        if (error.response?.status === 401) {
            // WebTMS determines user session has expired
            // but browser still has newtin cookie
            deleteAuthCookie();
            // Now we can forward to signin page
            router.push('/signin');
        }
        return Promise.reject(error);
    }
);

async function getNoCache(url) {
    let result = await axios.get(url, {
        // bypass browser cache
        headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
            Expires: '0',
        },
    });
    return result;
}
async function login(account, password) {
    let result = await axios.get(
        `${NEWTIN_HOST}/login?account=${encodeURIComponent(account)}&password=${encodeURIComponent(password)}`
    );

    if (result.data === 'ERR - Invalid login!') {
        return result.data;
    } else {
        const processLoginResponseResult = await processLoginResponse(
            result.data
        );
        return processLoginResponseResult.data;
    }
}
async function loginSSO(user) {
    let result = await axios.post(
        `${NEWTIN_HOST}/services/PROSERVE_TASKS.nap?oper=login-azure-sso&_=${new Date().getTime()}`,
        {
            id: user.id || '',
            displayName: user.displayName || '',
            userPrincipalName: user.userPrincipalName || '',
            mail: user.mail || '',
            companyName: user.companyName || '',
            street: user.street || '',
            streetAddress: user.streetAddress || '',
            city: user.city || '',
            state: user.state || '',
            postalCode: user.postalCode || '',
            officeLocation: user.officeLocation || '',
        }
    );

    if (result.data === 'ERR - Invalid login!') {
        return result.data;
    } else {
        const processLoginResponseResult = await processLoginResponse(
            result.data
        );
        return processLoginResponseResult.data;
    }
}

async function processLoginResponse(data) {
    let cookie = data.substr(12); // get value after 'Set-Cookie: '
    // strip out any 'expires=' section
    let range,
        start = cookie.indexOf('expires=');
    if (start > 0) {
        range = [start, cookie.indexOf(';', start)];
        cookie =
            cookie.substr(0, range[0]).trim() + cookie.substr(range[1] + 1);
    }
    cookie = cookie.split(';')[0];
    document.cookie = cookie;
    let result;
    try {
        result = axios.get(
            `${NEWTIN_HOST}` +
            '/NewtinWeb/proServe_NJS.njs?oper=getdata' +
            `&_=${new Date().getTime()}` +
            '&sets='+encodeURIComponent('user,centers,company,userOptions,userPermissionsEx,userPermissionsCatalog,userMessagesView,companyOptions,companyPermissions,systemVariables,companyCenters,companyLayers,companyMemberCodes,companyTicketPriorities,companyTicketTypes,counties,companyUsers,ticketColumnsCatalog,companyResponseCodes,centersPosRespCodes,companyPositiveResponseMapEx,userClipboard,companyTicketCategories,companyEmailSupplemental,reportHistory,companyBilling,sysReports'),
            {
                withCredentials: true,
            }
        );
    } catch (error) {
        console.error(
            new Error('processLoginResponse failed with ' + error.message)
        );
        return false;
    }
    return result;
}

async function validateResetPasswordToken(token) {
    let result = await axios.post(
        `${NEWTIN_HOST}/script/proServePWR.njs?oper=validate-reset-password-token`,
        {
            token,
        }
    );
    return result.data;
}

async function resetPasswordWithToken(token, newPassword) {
    let result = await axios.post(
        `${NEWTIN_HOST}/script/proServePWR.njs?oper=reset-password-from-token`,
        {
            token,
            newPassword,
        }
    );

    return result.data;
}

async function requestResetEmail(key) {
    let result = await axios.post(
        `${NEWTIN_HOST}/script/proServePWR.njs?oper=reset-request`,
        {
            key: key,
        }
    );
    return result.data;
}

async function getTicketAlerts() {
    let result = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/api/njsCompat/wtmTicketAlerts?_=${new Date().getTime()}`,
        {
            operation: 'get-list',
        }
    );
    return result.data;
}

async function dismissTicketAlert(rowid) {
    let result = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/api/njsCompat/wtmTicketAlerts?_=${new Date().getTime()}`,
        {
            operation: 'dismiss',
            rowid,
        }
    );
    return result.data;
}

async function dismissAllTicketAlerts() {
    let result = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/api/njsCompat/wtmTicketAlerts?_=${new Date().getTime()}`,
        {
            operation: 'dismiss-all',
        }
    );
    return result.data;
}

async function getLocatorVacation() {
    let result = await axios.get(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=getdata` +
        `&_=${new Date().getTime()}` +
        '&sets=locatorVacations',
        {
            withCredentials: true,
        }
    );
    return result.data;
}
async function getUserMessagesView() {
    let result = await axios.get(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=getdata` +
        `&_=${new Date().getTime()}` +
        '&sets=userMessagesView',
        {
            withCredentials: true,
        }
    );
    return result.data.userMessagesView;
}

async function getCompanyBilling() {
    let result = await axios.get(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=getdata` +
        `&_=${new Date().getTime()}` +
        '&sets=companyBilling',
        {
            withCredentials: true,
        }
    );
    return result.data.userMessagesView;
}

async function dismissUnreadMessage(message) {
    return message;
}

async function getDashboardData() {
    // set up a promise for getting data for most of the dashboard
    const dashboardDataPromise = axios.get(
        `${NEWTIN_HOST}` +
        '/NewtinWeb/proServe_NJS.njs?oper=getdata' +
        `&_=${new Date().getTime()}` +
        '&sets=' + encodeURIComponent('counties,overviewOpenTickets,ticketsReceivedToday'),
        {
            withCredentials: true,
        }
    );

    const recentEmergencyTicketsPromise = getRecentEmergencyTicketData();

    // execute the promises in parallel and destructure the results
    const [
        dashboardDataResult,
        recentEmergencyTicketsResult,
    ] = await Promise.all([
        dashboardDataPromise,
        recentEmergencyTicketsPromise,
    ]);

    // add the recent emergency tickets data to the result set
    const result = {
        ...dashboardDataResult.data,
        recentEmergencyTickets: recentEmergencyTicketsResult.data.data,
    };

    return result;
}

async function getUserObject(userId) {
    const result = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=getUserObject&_=${new Date().getTime()}`,
        {
            uid: userId,
        }
    );

    return result.data;
}

async function setUserObject(user) {
    function fixIngroupCasing(input) {
        switch (input.toUpperCase()) {
            case 'SYSOP':
                return 'SYSOP';
            case 'DIRECTOR':
                return 'Director';
            case 'ADMINISTRATOR':
                return 'Administrator';
            case 'DISPATCHER':
                return 'Dispatcher';
            case 'LOCATOR':
                return 'Locator';
        }
    }

    const result = await axios.post(
        `${NEWTIN_HOST}` +
        '/NewtinWeb/proServe_NJS.njs?oper=setUserObject' +
        `&_=${new Date().getTime()}`,
        {
            uid: user.uid,
            cid: user.cid,
            account: user.account,
            password: '',
            passwordConfirm: '',
            ingroup: fixIngroupCasing(user.ingroup),
            homepage: user.homepage,
            name: user.name,
            company: user.company,
            address1: user.address1,
            address2: user.address2,
            city: user.city,
            state: user.state,
            zip: user.zip,
            phone: user.phone,
            fax: user.fax,
            cell: user.cell,
            email: user.email,
            remarks: user.remarks,
            birth: user.birth,
            death: user.death,
            allowed: true,
            readonly: false,
            deleted: false,
            userEmailCC: [],
            userOptions: [],
            userPermissions: [],
        },
        {
            withCredentials: true,
        }
    );

    return result.data;
}

// internal function, no need to export
function getRecentEmergencyTicketData() {
    // compute dates for today and for last week
    const today = new Date();
    const todaysDate = today.toISOString().split('T')[0];
    const lastWeek = new Date(today.setDate(today.getDate() - 14));
    const lastWeeksDate = lastWeek.toISOString().split('T')[0];

    // set up a promise for getting raw data for recent emergency tickets table,
    // raw data will need to be formatted by the component using the data
    return axios.post(
        `${NEWTIN_HOST}` +
        '/NewtinWeb/proServe_NJS.njs?oper=wtmOpenTickets' +
        `&_=${new Date().getTime()}`,
        {
            fields: 'TR_member_code,ticket,revision,TS_stage,duedate,seq_date,received,TS_ticket_priority,TS_ticket_type,TS_ticket_category,company,work_type,donefor,ticket_date',
            orderBy: 'received-desc',
            pageInfo: {
                paged: false,
                page: 1,
                pageSize: 500,
            },
            filters: {
                TR_stage: 'ALL',
                seq_date: `${lastWeeksDate}:${todaysDate}`,
                stype: 'emergency,damage'
            },
        },
        {
            withCredentials: true,
        }
    );
}

async function heartbeat() {
    const result = await axios.post(`${NEWTIN_HOST}/parsley`);

    return result.data;
}

async function changeUserPassword(user) {
    let result = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=setUserChangePassword&_=${new Date().getTime()}`,
        {
            uid: user.uid,
            cid: user.cid,
            account: user.account,
            currentPassword: user.currentPassword,
            newPassword: user.newPassword,
            newPasswordConfirm: user.newPasswordConfirm
        }
    );
    return result.data;
}
function buildFieldList(fields) {
    let baseFields =
        'center,state,TR_stage,TR_member_code,TR_rowid,TR_response_code,ticket,revision,type,priority,category,TR_seq_date,TR_seq_num,TR_locator,county,place,company,work_type,duedate,address,street,cross1,cross2,work_date';
    let fieldList = baseFields.split();
    if (fields) {
        let checkFields = fields.split();
        for (let field of checkFields) {
            if (!fieldList.includes(field)) {
                fieldList.push(field);
            }
        }
    }
    return fieldList.join();
}
async function getTicketCount(filters) {
    let result = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=wtmOpenTickets&_=${new Date().getTime()}`,
        {
            fields: 'TR_rowid',
            orderBy: 'place-desc',
            orderByDefault: 'seq_date-asc',
            autoRefreshMinutes: 0,
            pageInfo: {
                paged: true,
                page: 1,
                pageSize: 1,
                rowCount: 0,
                rowStart: 0,
                pageCount: 0,
            },
            filters: filters,
        }
    );
    return result.data;
}

async function getTickets(page, pageSize, fields, orderBy, filters) {
    let result = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=wtmOpenTickets&_=${new Date().getTime()}`,
        {
            fields: buildFieldList(fields),
            orderBy: orderBy || 'place-desc',
            orderByDefault: 'seq_date-asc',
            autoRefreshMinutes: 0,
            pageInfo: {
                paged: true,
                page: page,
                pageSize: pageSize,
                rowCount: 0,
                rowStart: 0,
                pageCount: 0,
            },
            filters: filters,
        }
    );
    return result.data;
}

async function updateClipboardItem(operation, rowId) {
    let result = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=wtmClipboard&_=${new Date().getTime()}`,
        {
            oper: operation,
            TR_rowid: rowId,
        }
    );
    return result.data;
}

async function deleteMessage(rowid) {
    const result = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=deleteCompanyMessage&_=${new Date().getTime()}`,
        {
            rowid,
        }
    );
    return result.data;
}
async function getCompanyMessage(row_id) {
    let result = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=getCompanyMessage&_=${new Date().getTime()}`,
        {
            rowid: row_id
        }
    );
    return result.data;
}

async function addNewTicketTab(data) {
    // 1: "{\n  \"_TIMESTAMP_\": \"2021-12-25T14:03:11.619Z\",\n  \"tabClass\": \"open-tickets\",\n  \"tabMoniker\": \"1640440991233\",\n  \"tabId\": \"open-tickets--1640440991233\",\n  \"label\": \"Tickets-3\",\n  \"config\": {\n    \"autoRefreshMinutes\": 0,\n    \"pageSize\": 100,\n    \"orderBy\": \"seq_date-asc\",\n    \"orderByDefault\": \"seq_date-asc\",\n    \"fields\": \"center,TR_member_code,ticket,revision,type,priority,TR_seq_date,TR_seq_num,TR_locator,county,place,company,work_type\",\n    \"filters\": {\n      \"[object Object]\": \"*\",\n      \"center\": \"45\",\n      \"member_code\": \"*\",\n      \"seq_date\": \"*\",\n      \"stype\": \"*\",\n      \"priority\": \"*\",\n      \"type\": \"*\",\n      \"category\": \"*\",\n      \"TR_stage\": \"*\",\n      \"fips\": \"*\",\n      \"locator\": \"*\",\n      \"due\": \"*\"\n    },\n    \"filtersDisplay\": {\n      \"center\": true,\n      \"member_code\": true,\n      \"seq_date\": true,\n      \"stype\": true,\n      \"priority\": true,\n      \"type\": true,\n      \"category\": true,\n      \"TR_stage\": true,\n      \"fips\": true,\n      \"locator\": true,\n      \"due\": true\n    }\n  }\n}"
    const newDate = new Date();
    const result = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=setUserOptions&_=${new Date().getTime()}`,
        {
            options: [
                [
                    `JSON:TAB:open-tickets--${newDate.getTime()}`,
                    JSON.stringify({
                        _TIMESTAMP_: `${newDate.toISOString()}`,
                        tabClass: 'open-tickets',
                        tabMoniker: `${new Date().getTime()}`,
                        tabId: `open-tickets--${newDate.getTime()}`,
                        label: data.label,
                        config: {
                            autoRefreshMinutes: 0,
                            pageSize: 100,
                            orderBy: 'seq_date-asc',
                            orderByDefault: 'seq_date-asc',
                            fields: defaultTicketFields,
                            
                            filters: data.config?.filters || defaultTicketFilters,
                            filtersDisplay: defaultFiltersDisplay,
                        },
                    }),
                ],
            ],
        }    );
    return result.data;
}
async function getTicketPageData() {
    const result = await axios.get(
        `${NEWTIN_HOST}` +
        '/NewtinWeb/proServe_NJS.njs?oper=getdata' +
        `&_=${new Date().getTime()}` +
        '&sets=' + encodeURIComponent('company,userOptions,overviewOpenTickets,userPermissionsEx,companyOptions,companyPermissions,companyTicketPriorities,companyTicketTypes,ticketColumnsCatalog,companyResponseCodes,centersPosRespCodes,companyPositiveResponseMapEx,userClipboard,companyCenters,companyBilling,sysReports'),
        {
            withCredentials: true,
        }
    );
    return result.data;
}

async function getUserClipboard() {
    const result = await axios.get(
        `${NEWTIN_HOST}` +
        '/NewtinWeb/proServe_NJS.njs?oper=getdata' +
        `&_=${new Date().getTime()}` +
        `&sets=userClipboard`,
        {
            withCredentials: true,
        }
    );
    return result.data;
}
async function getUserOptions() {
    try {
        const result = await axios.get(
            `${NEWTIN_HOST}` +
            '/NewtinWeb/proServe_NJS.njs?oper=getdata' +
            `&_=${new Date().getTime()}` +
            '&sets=' + encodeURIComponent('userOptions,overviewOpenTickets'),
            {
                withCredentials: true,
            }
        );
        return result.data;
    } catch (error) {
        console.error(new Error('getUserOptions failed with ' + error.message));
        return false;
    }
}

async function deleteUserOption(id) {
    try {
        const result = axios.post(
            `${NEWTIN_HOST}` +
            '/NewtinWeb/proServe_NJS.njs?oper=deleteUserOptions' +
            `&_=${new Date().getTime()}`,
            {
                options: [id],
            },
            {
                withCredentials: true,
            }
        );
        return result;
    } catch (error) {
        console.error(
            new Error('processLoginResponse failed with ' + error.message)
        );
        return false;
    }
}

async function downloadTickets(format, tickets) {
    let response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/PROSERVE_TASKS.NAP?oper=ticket-export&format=${format}&_=${new Date().getTime()}`,
        'tickets=' + encodeURIComponent(tickets.toLowerCase()),
        {
            responseType: 'arraybuffer',
        }
    );
    let header = response.request.getResponseHeader('Content-Disposition');
    header = header.split('=');
    header = header[1];
    let fileName = header.replaceAll('"', '');
    let blob = new Blob([response.data], { type: 'application/txt' });
    let link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
}

async function printTickets(action, param) {
    try {
        const result = await axios.post(
            `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=wtmPrintPDF&_=${new Date().getTime()}`,
            {
                action: action,
                param: param,
            },
            {
                withCredentials: true,
            }
        );
        return result.data;
    } catch (error) {
        console.error(
            new Error('processLoginResponse failed with ' + error.message)
        );
        return false;
    }
}
async function emailTickets(payload) {
    // payload:
    /*
        {
            "destination": [
                "third@mail.com"
            ],
            "tickets": [
                {
                "tid": "{FC7AFFE2-B391-4B56-88CA-999D7FD00FD2}",
                "member_code": "SCG76"
                }
            ],
            "format": "ORIGINAL"
        }
    */
    let response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=wtmEmailTickets&_=${new Date().getTime()}`,
        payload
    );
    return response.data;
}
async function smsTickets(payload) {
    // payload:
    /*
       { format: 'SMSTEXT', destination: '919-961-1661', tickets: [ { tid: 'value', member_code: 'MBR' }, ... ] }
    */
    let response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=wtmSMSTickets&_=${new Date().getTime()}`,
        payload
    );
    return response.data;
}

async function postResponse(payload) {
    let response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=wtmRespondTickets&_=${new Date().getTime()}`,
        payload
    );
    return response.data;
}

async function getTicketDetails(tid) {
    try {
        const result = await axios.get(
            `${NEWTIN_HOST}/NewtinWeb/api/njsCompat/wtmGetTicket?tid=${encodeURIComponent(guidNormal(tid))}&_=${new Date().getTime()}`,
            {
                withCredentials: true,
            }
        );
        return result.data;
    } catch (error) {
        console.error(
            new Error('getTicketDetails failed with ' + error.message)
        );
        return false;
    }
}

async function setUserOptions(options) {
    let result = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/api/njsCompat/wtmSetUserOptions?_=${new Date().getTime()}`,
        {
            options,
        },
        {
            withCredentials: true,
        }
    );

    return result.data;
}

async function getTicketResponse(rowid) {
    const result = await axios.get(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=getTicketResponse&_=${new Date().getTime()}&rowid=${encodeURIComponent(guidNormal(rowid))}`
    );

    return result.data;
}

async function postAssignTicket(payload) {
    let response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=wtmAssignTickets&_=${new Date().getTime()}`,
        payload
    );
    return response.data;
}

async function addNote(payload) {
    let response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=ticketNotes&_=${new Date().getTime()}`,
        payload
    );
    return response.data;
}

async function uploadImage(formData, config) {
    const response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=uploadImage&_=${new Date().getTime()}`,
        formData,
        config
    );
    return response.data;
}

async function deleteImage(payload) {
    const response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=ticketImages&_=${new Date().getTime()}`,
        payload
    );
    return response.data;
}

async function editImage(payload) {
    const response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=ticketImages&_=${new Date().getTime()}`,
        payload
    );
    return response.data;
}

async function getMappingDirectory(state, county) {
    const result = await axios.post(
        `${NEWTIN_HOST}/mapping`,
        `qmcmd;getdirectory;${state};${county};`
    );
    return result.data;
}

async function getMapImage(
    width,
    height,
    state,
    county,
    centery,
    centerx,
    altitude,
    mrsid,
    getters
) {
    let result;
    if (getters && getters.forceOffline) {
        const url = `/NewtinWeb/proServe_NJS.njs?oper=getMapImage&tid=${encodeURIComponent(guidNormal(getters.ticketDetails.Tickets.tid))}&ortho`;
        result = await axios.get(`${NEWTIN_HOST}${url}`);
        result = {
            data: `OK;${url},${result.headers['x-lat-long-bounding-box']}`,
            host: NEWTIN_HOST,
        };
    } else {
        result = await axios.post(
            `${NEWTIN_HOST}/mapping`,
            `qmcmd;zoom;${width};${height};${state};${county};${centery};${centerx};${altitude};${mrsid}`
        );
    }
    return result;
}

async function getMapImageOffline(tid) {
    const result = await axios.get(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=getMapImage&tid=${encodeURIComponent(guidNormal(tid))}&ortho`
    );
    return result;
}

async function getLayerData(layers, map) {
    const result = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=getLayerDataEx`,
        {
            layers: layers,
            map: {
                top: map.top,
                bottom: map.bottom,
                left: map.left,
                right: map.right,
            }
        }
    );
    return result.data;
}
async function getCompanyLayersEx() {
    try {
        const result = await axios.get(
            `${NEWTIN_HOST}` +
            '/NewtinWeb/proServe_NJS.njs?oper=getdata' +
            `&_=${new Date().getTime()}` +
            '&sets=companyLayersEx'
        );
        return result;
    } catch (error) {
        console.error(new Error('getUserOptions failed with ' + error.message));
        return false;
    }
}

async function getAutoResponseRules(response_code) {
    const result = await axios.post(
        `${NEWTIN_HOST}` +
        '/NewtinWeb/proServe_NJS.njs?oper=getResponseCodeAutoResponseRules' +
        `&_=${new Date().getTime()}`,
        {
            response_code: response_code
        }
    );
    return result.data;
}

async function updateAutoResponseRules(payload) {
    const result = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=updateAutoResponseRule&_=${new Date().getTime()}`,
        payload
    );
    return result.data;
}

async function deleteAutoResponseRule(payload) {
    const result = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=deleteAutoResponseRule&_=${new Date().getTime()}`,
        payload
    );
    return result.data;
}

async function addUser(data) {
    const result = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=createUser&_=${new Date().getTime()}`,
        {
            ...data
        }
    );
    return result.data;
}

async function updateUser(data) {
    const result = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=setUserObject&_=${new Date().getTime()}`,
        {
            ...data
        }
    );
    return result.data;
}

async function getUser(uid) {
    const result = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=getUserObject&_=${new Date().getTime()}`,
        {
            uid: uid
        }
    );
    return result.data;
}

async function getCompanyUsers() {
    const result = await axios.get(
        `${NEWTIN_HOST}` +
        '/NewtinWeb/proServe_NJS.njs?oper=getdata' +
        `&_=${new Date().getTime()}` +
        '&sets=companyUsers',
        {
            withCredentials: true,
        }
    );
    return result.data;
}
async function fetchAddressPoints(state, county, layer, addressField, map) {
    const result = await axios.post(
        `${NEWTIN_HOST}/mapping`,
        `qmcmd;getshapes;${state};${county};${layer};shapetype:${addressField};;${map.top};${map.bottom};${map.left};${map.right};False`
    );
    // 40.7980028606857;40.7911629129596;-81.4487368361622;-81.4352737610663;False`);
    return result.data;
}
async function getImageData(url, type) {
    const valid_mimetypes = {
        'jpg': 'image/jpeg',
        'jpeg': 'image/jpeg',
        'png': 'image/png',
        'tiff': 'image/tiff',
        'tif': 'image/tiff',
        'pdf': 'application/pdf',
        'gif': 'image/gif',
        'heic': 'image/heic'
    };
    if(valid_mimetypes[type]) {
        const response = await axios.get(url, {
            responseType: 'arraybuffer',
            withCredentials: false,
        });
        return (
            `data:${valid_mimetypes[type]};base64,` +
            Buffer.from(response.data, 'binary').toString('base64')
        );
    } else {
        const response = await axios.get(url, { withCredentials: false });
        return response.data;
    }
}

async function fetchParcels(state, county, layer, map) {
    const result = await axios.post(
        `${NEWTIN_HOST}/mapping`,
        `qmcmd;getshapes;${state};${county};${layer};shapetype:addr_numb:addr_full;;${map.top};${map.bottom};${map.left};${map.right};False`
    );

    return result.data;
}

async function fetchReverseStreet(
    state,
    county,
    latitude,
    longitude,
    altitude
) {
    const result = await axios.get(
        `${NEWTIN_HOST}${NEWTIN_GEOCODER}?oper=getreversestreet&state=${state}&county=${county}&latitude=${latitude}&longitude=${longitude}&altitude=${altitude}`
    );
    return Function(`return ${result.data}`)();
}

async function getOverviewAutoAssignRules() {
    let result = await axios.get(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=getdata` +
        `&_=${new Date().getTime()}` +
        '&sets=overviewAutoAssignRules',
        {
            withCredentials: true,
        }
    );
    return result.data;
}
async function getAutoAssignRules(uid) {
    const result = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=getLocatorAutoAssignRules&_=${new Date().getTime()}`,
        {
            uid: uid
        }
    );
    return result.data;
}

async function createMapLayer(form_data,addEsriLayer) {
    let response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=createMapLayer`,
        {
            name: form_data.name,
            description: form_data.description,
            style: {
                show: !!form_data.style.show,
                color: form_data.style.color
            },
            external_layer_json: addEsriLayer ? JSON.stringify(form_data.external_layer_json): ''           
        }
    );
    return response.data;
}
async function updateMapLayer(form_data, addEsriLayer) {
    let response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=updateMapLayer`,
        {
            layer_id: form_data.layer_id,
            name: form_data.name,
            description: form_data.description,
            style: {
                show: !!form_data.style.show,
                color: form_data.style.color,
            },
            external_layer_json: addEsriLayer ? JSON.stringify(form_data.external_layer_json): '' 
        }
    );
    return response.data;
}

async function deleteMapLayer(layer_id) {
    let response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=deleteMapLayer`,
        {
            layer_id: layer_id            
           
        }
    );
    return response.data;
}

async function uploadMapLayer(form_data) {
    let response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/PROSERVE_TASKS.nap?oper=shapefile-upload`,
        form_data
    );
    return response.data;
}

async function saveMapLayerFeatures(features) {
    let response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=layerPolygon`,
        {
            ...features
        }
    );
    return response.data;
}

async function updateAutoAssignRule(form_data) {
    let response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=updateAutoAssignRule&_=${new Date().getTime()}`,
        form_data
    );
    return response.data;
}
async function deleteAutoAssignRule(row_id) {
    let response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=deleteAutoAssignRule&_=${new Date().getTime()}`,
        {
            rowid: row_id
        }
    );
    return response.data;
}

async function updateTieBreaking(value) {
    let response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=setCompanyOptions&_=${new Date().getTime()}`,
        {
            options: [['AUTOASSIGN_TIEBREAK_METHOD', value]],
        }
    );
    return response.data;
}

async function addBillingCode(data) {
    let response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=wtmBillingCode&_=${new Date().getTime()}`,
        {
            oper: 'add',
            param: data  // { enabled: true, code: "test", amount: "150.00", description: "Description" }
        }
    );
    return response.data;
}

async function updateBillingCode(data) {
    let response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=wtmBillingCode&_=${new Date().getTime()}`,
        {
            oper: 'update',
            param: data  // { rowid: '<guid>', enabled: true, code: "test", amount: "150.00", description: "Description" }
        }
    );
    return response.data;
}

async function deleteBillingCode(data) {
    let response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=wtmBillingCode&_=${new Date().getTime()}`,
        {
            oper: 'remove',
            param: data  // { rowid: "<guid>" }
        }
    );
    return response.data;
}

async function updateResponseBilling(data) {
    let response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=wtmTicketResponsBilling&_=${new Date().getTime()}`,
        {            
            oper: 'update',
            param: {
                ...data 
            }
        }
    );
    return response.data;
}
async function addResponseBilling(data) {
    let response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=wtmTicketResponsBilling&_=${new Date().getTime()}`,
        {            
            oper: 'add',
            param: {
                ...data 
            }
        }
    );
    return response.data;
}
async function deleteResponseBilling(data) {
    let response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=wtmTicketResponsBilling&_=${new Date().getTime()}`,
        {            
            oper: 'remove',
            param: {
                ...data 
            }
        }
    );
    return response.data;
}

async function addMemberCode(data) {
    let response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=addCompanyMemberCode&_=${new Date().getTime()}`,
        {
            ...data
        }
    );
    return response.data;
}

async function editMemberCode(data) {
    let response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=updateCompanyMemberCode&_=${new Date().getTime()}`,
        {
            ...data
        }
    );
    return response.data;
}

async function deleteMemberCode(data, cid) {
    let response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=delCompanyMemberCode&_=${new Date().getTime()}`,
        {
            cid: cid,
            remove: [...data]
        }
    );
    return response.data;
}

async function addResponseCode(data) {
    let response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=responseCode&_=${new Date().getTime()}`,
        {
            ...data
        }
    );
    return response.data;
}

async function editResponseCode(data) {
    let response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=responseCode&_=${new Date().getTime()}`,
        {
            ...data
        }
    );
    return response.data;
}

async function deleteResponseCode(data) {
    let response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=responseCode&_=${new Date().getTime()}`,
        {
            ...data
        }
    );
    return response.data;
}

async function getArchives() {
    const result = await axios.get(
        `${NEWTIN_HOST}` +
        '/NewtinWeb/proServe_NJS.njs?oper=getdata' +
        `&_=${new Date().getTime()}` +
        '&sets=companyArchives'
    );
    return result.data;
}
async function getCompanyStatsCompanyEOD() {
    const response = await axios.get(
        `${NEWTIN_HOST}` +
        '/NewtinWeb/proServe_NJS.njs?oper=getdata' +
        `&_=${new Date().getTime()}` +
        '&sets=companyStatsEODReports'
    );
    return response.data;
}
async function getCompanyCompanyEODList(month) {
    const response = await axios.post(
        `${NEWTIN_HOST}` +
        '/NewtinWeb/proServe_NJS.njs?oper=getCompanyEodReportsList' +
        `&_=${new Date().getTime()}`,
        {
            month: month
        }
    );
    return response.data;
}
async function getCompanyEODReport(rowid) {
    const response = await axios.get(
        `${NEWTIN_HOST}` +
        `/NewtinWeb/proServe_NJS.njs?oper=getAttachment&rowid=${encodeURIComponent(guidNormal(rowid))}`
    );
    return response.data;
}
async function getCompanyStatsEODAudit() {
    const response = await axios.get(
        `${NEWTIN_HOST}` +
        '/NewtinWeb/proServe_NJS.njs?oper=getdata' +
        `&_=${new Date().getTime()}` +
        '&sets=companyStatsEodAudit'
    );
    return response.data;
}

async function getCompanyEODAuditsList(month) {
    const response = await axios.post(
        `${NEWTIN_HOST}` +
        '/NewtinWeb/proServe_NJS.njs?oper=getCompanyEodAuditsList' +
        `&_=${new Date().getTime()}`,
        {
            month: month
        }
    );
    return response.data;
}

async function getCompayEODAudit(rowid) {
    let response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=getCompanyEodAudit&_=${new Date().getTime()}`,
        {
            rowid: rowid
        }
    );
    return response.data;
}
async function getEvents(cid, page, pageSize) {
    let response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=getEvents&_=${new Date().getTime()}`,
        {
            cid: cid,
            orderBy: 'created',
            orderByDir: 'DESC',
            page: page,
            pageSize: pageSize,
            rowStart: 1
        }
    );
    return response.data;
}

async function getEmailList() {
    let response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=getEmailList&_=${new Date().getTime()}`,
        {
        }
    );
    return response.data;
}

async function getEmailHistory() {
    let response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=getEmailHistory&_=${new Date().getTime()}`,
        {
        }
    );
    return response.data;
}

async function getQueuePositiveResponse() {
    let response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=queuePositiveResponse&_=${new Date().getTime()}`,
        {
        }
    );
    return response.data;
}

async function getFullTickets(tid) {
    let response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=wtmFullTicket&_=${new Date().getTime()}`,
        {
            tid: tid
        }
    );
    return response.data;
}

async function supplementalMessageRule(data) {
    let response = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=supplementalEmail&_=${new Date().getTime()}`,
        {
            ...data
        }
    );
    return response.data;
}

async function getCompanyEmailSupplemental() {
    let result = await axios.get(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=getdata` +
        `&_=${new Date().getTime()}` +
        '&sets=companyEmailSupplemental'
    );
    return result.data.companyEmailSupplemental;
}

async function generateReport(param, report) {
    // determine which report function to call
    let fnReport = report.startsWith('TMS/') ? 'wtmReportTMS' : 'wtmReportSSRS';
    report = report.replace('TMS/', '');
    let result = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=${fnReport}&_=${new Date().getTime()}`,
        {
            'action': 'run',
            'report': report,
            'param': param
        }
    );
    return result.data;
}
async function generateReportTMS(param, report) {
    let result = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=wtmReportTMS&_=${new Date().getTime()}`,
        {
            'action': 'run',
            'report': report,
            'param': param
        }
    );
    return result.data;
}

async function checkReportStatus(rowId, report) {
    let result = await axios.post(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=wtmReportSSRS&_=${new Date().getTime()}`,
        {
            'action': 'status',
            'report': report,
            'param': {
                rowid: rowId
            }
        }
    );
    return result.data;
}

async function getReportHistory() {
    let result = await axios.get(
        `${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=getdata` +
        `&_=${new Date().getTime()}` +
        '&sets=reportHistory',
        {
            withCredentials: true,
        }
    );
    return result.data;
}
async function reOpenTicket(payload) {
    try {
        await Promise.all(payload.map(item => handleTicket({ rowid: [item] })));
    } catch (error) {
        console.error(error);
    }
}
  
async function handleTicket(ticket) {
    const result = await axios.post(`${NEWTIN_HOST}/NewtinWeb/proServe_NJS.njs?oper=reOpenTicketEx&_=${new Date().getTime()}`, ticket);
    return result.output;
}

// validate ESRI layer
async function validateEsriLayer(layerUrl, apiToken, label) {
    let isValid = false;
    let message = 'Layer is invalid';

    try {
        const response = await fetch(`${layerUrl}/query/?f=json&returnGeometry=true&spatialRel=esriSpatialRelIntersects&geometry=-180,-90,180,90&geometryType=esriGeometryEnvelope&inSR=4326&outFields=*&outSR=4326&token=${apiToken}&resultOffset=0&resultRecordCount=1`);

        if (!response.ok) {
            throw new Error(`HTTP error: ${response.status} - ${response.statusText}`);
        }

        const res = await response.json();

        if (res.error) {
            throw new Error(`API error: ${res.error.message}`);
        }

        const { geometryType, fields, features } = res;

        if (!geometryType || !fields || !features) {
            throw new Error('Invalid response format');
        }

        let lat, lon;

        try {
            const geometry = features[0].geometry;

            switch (geometryType) {
                case 'esriGeometryPoint':
                    [lon, lat] = geometry?.x && geometry?.y ? [geometry.x, geometry.y] : [undefined, undefined];
                    break;
                case 'esriGeometryPolyline':
                    [lon, lat] = geometry?.paths && geometry.paths[0][0] ? geometry.paths[0][0] : [undefined, undefined];
                    break;
                case 'esriGeometryPolygon':
                    [lon, lat] = geometry?.rings && geometry.rings[0][0] ? geometry.rings[0][0] : [undefined, undefined];
                    break;
                default:
                    throw new Error(`Unsupported geometry type: ${geometryType}`);
            }

            isValid = true;
            message = 'Layer is valid';
        } catch (error) {
            message = `Geometry error: ${error.message}`;
        }

        const labels = fields.map(f => f.name);

        if (label && !labels.includes(label)) {
            isValid = false;
            message = `Label ${label} not found in layer`;
        }

        return { isValid, message, coord: [lon, lat], labels, res };
    } catch (error) {
        message = `Validation error: ${error.message}`;
        return { isValid, message };
    }
}

export default {
    getNoCache,
    login,
    loginSSO,
    validateResetPasswordToken,
    resetPasswordWithToken,
    requestResetEmail,
    getTicketAlerts,
    getTickets,
    getTicketPageData,
    updateClipboardItem,
    getTicketCount,
    dismissTicketAlert,
    dismissAllTicketAlerts,
    getDashboardData,
    getLocatorVacation,
    getUserMessagesView,
    dismissUnreadMessage,
    heartbeat,
    getUserObject,
    setUserObject,
    setUserOptions,
    changeUserPassword,
    getCompanyMessage,
    deleteMessage,
    addNewTicketTab,
    getUserOptions,
    deleteUserOption,
    downloadTickets,
    printTickets,
    emailTickets,
    postResponse,
    getTicketDetails,
    getTicketResponse,
    postAssignTicket,
    addNote,
    uploadImage,
    deleteImage,
    editImage,
    getMappingDirectory,
    getMapImage,
    getMapImageOffline,
    getLayerData,
    getAutoResponseRules,
    updateAutoResponseRules,
    deleteAutoResponseRule,
    addUser,
    updateUser,
    getUser,
    getCompanyUsers,
    getImageData,
    fetchAddressPoints,
    fetchParcels,
    fetchReverseStreet,
    getOverviewAutoAssignRules,
    getAutoAssignRules,
    getCompanyLayersEx,
    createMapLayer,
    updateMapLayer,
    uploadMapLayer,
    deleteMapLayer,
    updateTieBreaking,
    updateAutoAssignRule,
    deleteAutoAssignRule,
    addBillingCode,
    updateBillingCode,
    deleteBillingCode,
    updateResponseBilling,
    addResponseBilling,
    deleteResponseBilling,
    addMemberCode,
    editMemberCode,
    deleteMemberCode,
    addResponseCode,
    editResponseCode,
    deleteResponseCode,
    getArchives,
    getCompanyStatsCompanyEOD,
    getCompanyCompanyEODList,
    getCompanyEODReport,
    getCompanyStatsEODAudit,
    getCompanyEODAuditsList,
    getCompayEODAudit,
    getCompanyBilling,
    getEmailList,
    getEmailHistory,
    getQueuePositiveResponse,
    saveMapLayerFeatures,
    getFullTickets,
    supplementalMessageRule,
    getCompanyEmailSupplemental,
    getReportHistory,
    getUserClipboard,
    generateReport,
    checkReportStatus,
    getEvents,
    reOpenTicket,
    generateReportTMS,
    smsTickets,
    validateEsriLayer,
};
